import { decamelize } from 'helpers/camelize';

import {
  fetchWrapper,
  objectToQueryString,
  handleResponse,
  formatBody,
  buildOpts,
  hcUrl,
  hcUrlV3,
  transformBasketResponse,
  transformPastOrdersResponse,
  transformReceiptResponse,
  transformProjectedCosts,
} from '../helpers';

const getBasketSummaries = async ({ params }) => {
  const response = await fetchWrapper(
    hcUrl(`/weekly_baskets/summaries?${objectToQueryString(decamelize(params))}`),
    buildOpts()
  );
  return handleResponse(response);
};

const getBasket = async ({ params: { menuSlug } }) => {
  const response = await fetchWrapper(
    hcUrl(`/menus/${menuSlug}/weekly_basket?remove_meal_details=true`),
    buildOpts()
  );
  return handleResponse(response, transformBasketResponse);
};

const editBasket = async ({ method, params }) => {
  const { menuSlug, ...restParams } = params;

  const response = await fetchWrapper(
    hcUrl(`/menus/${menuSlug}/weekly_basket`),
    buildOpts({
      method,
      body: formatBody(restParams),
    })
  );

  return handleResponse(response, transformBasketResponse);
};

const donateBasket = async ({ params }) => {
  const { menuSlug, servings } = params;

  const response = await fetchWrapper(
    hcUrl(`/menus/${menuSlug}/weekly_basket/donate`),
    buildOpts({
      method: 'PATCH',
      body: formatBody({ weeklyBasket: { servings } }),
    })
  );

  return handleResponse(response, transformBasketResponse);
};

const addBasket = ({ params }) => editBasket({ params, method: 'POST' });

const updateBasket = ({ params }) => editBasket({ params, method: 'PATCH' });

const updateBasketAddress = async ({ params }) => {
  const { menuSlug, address } = params;

  const response = await fetchWrapper(
    hcUrl(`/weekly_baskets/${menuSlug}/addresses`),
    buildOpts({
      method: 'PATCH',
      body: formatBody({ basketAddress: address, newAddress: false }),
    })
  );
  return handleResponse(response, transformBasketResponse);
};

const undonateBasket = async ({ params }) => {
  const { menuSlug } = params;

  const response = await fetchWrapper(
    hcUrl(`/menus/${menuSlug}/weekly_basket/cancel_donation`),
    buildOpts({
      method: 'PATCH',
    })
  );

  return handleResponse(response, transformBasketResponse);
};

const skipBasket = async ({ params }) => {
  const { menuSlug, ...restParams } = params;

  const response = await fetchWrapper(
    hcUrl(`/weekly_baskets/${menuSlug}/skips`),
    buildOpts({
      method: 'POST',
      body: formatBody(restParams),
    })
  );

  return handleResponse(response);
};

const unskipBasket = async ({ params }) => {
  const { menuSlug, ...restParams } = params;

  const response = await fetchWrapper(
    hcUrl(`/weekly_baskets/${menuSlug}/skips`),
    buildOpts({
      method: 'DELETE',
      body: formatBody(restParams),
    })
  );

  return handleResponse(response, transformBasketResponse);
};

const getTracking = async ({ params: { menuSlug } }) => {
  const response = await fetchWrapper(hcUrl(`/weekly_baskets/${menuSlug}/tracking`), buildOpts());

  return handleResponse(response);
};

const getReceipt = async ({ params }) => {
  const response = await fetchWrapper(
    hcUrlV3(`/weekly_basket/receipt?${objectToQueryString(decamelize(params))}`),
    buildOpts()
  );

  return handleResponse(response, transformReceiptResponse);
};

const getNutritionSummary = async ({ params }) => {
  const response = await fetchWrapper(
    hcUrl(`/weekly_basket/nutrition_summary?${objectToQueryString(decamelize(params))}`),
    buildOpts()
  );

  return handleResponse(response);
};

const getPastOrders = async ({ params: { page } }) => {
  const response = await fetchWrapper(
    hcUrl(`/weekly_baskets?page=${page}&per_page=10`),
    buildOpts()
  );

  return handleResponse(response, transformPastOrdersResponse);
};

const getProjectedCosts = async ({ params }) => {
  const response = await fetchWrapper(
    hcUrlV3(`/weekly_baskets/projected_costs?${objectToQueryString(decamelize(params, true))}`),
    buildOpts()
  );

  return handleResponse(response, transformProjectedCosts);
};

const getTieredPricingDetails = async ({ params }) => {
  const response = await fetchWrapper(hcUrlV3(`/meal_price_plans/${params.id}`), buildOpts());

  return handleResponse(response);
};

export {
  addBasket,
  donateBasket,
  getBasketSummaries,
  getReceipt,
  getNutritionSummary,
  getBasket,
  getPastOrders,
  getTracking,
  getProjectedCosts,
  editBasket,
  skipBasket,
  updateBasket,
  updateBasketAddress,
  undonateBasket,
  unskipBasket,
  getTieredPricingDetails,
};
